import { CONSENT_EVENT, effectiveConsent } from "@/lib/consent";

const STORAGE_KEY = "smartly_attr_v1";
const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
export const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "msclkid",
  "fbclid",
  "campaignid",
  "adgroupid",
  "matchtype",
  "device",
  "network",
  "v",
] as const;
const CLICK_IDS = new Set(["gclid", "gbraid", "wbraid", "msclkid", "fbclid"]);

export type AttributionParams = Partial<
  Record<(typeof ATTRIBUTION_PARAMS)[number], string>
>;
export type AttributionTouch = {
  params: AttributionParams;
  landing_path: string;
  referrer: string;
  ts: string;
};
type AttributionState = { first: AttributionTouch; last: AttributionTouch };
let state: AttributionState | null = null;
let listening = false;
const removeStored = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Storage may be unavailable. */
  }
};

export function sanitizeAttributionValue(value: string, max = 255) {
  return value
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, max);
}

/** Unfilled ad placeholders like "{campaignid}" or test text like "test123". */
export function isPlaceholderValue(value: string) {
  return /[{}]/.test(value) || /^test[-_]?(\d+|ad|campaign|adgroup)?$/i.test(value);
}

function persist() {
  if (!effectiveConsent().analytics || !state) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* Attribution must not block navigation. */
  }
}

function restore() {
  if (!effectiveConsent().analytics) return;
  try {
    const parsed = JSON.parse(
      localStorage.getItem(STORAGE_KEY) || "null",
    ) as AttributionState | null;
    if (
      !parsed?.first?.params ||
      !parsed?.last?.params ||
      !Number.isFinite(Date.parse(parsed.last.ts)) ||
      Date.now() - Date.parse(parsed.last.ts) > MAX_AGE
    ) {
      removeStored();
      return;
    }
    state = parsed;
  } catch {
    removeStored();
  }
}

export function captureAttribution(url = new URL(window.location.href)) {
  if (!state) restore();
  const params: AttributionParams = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const cleaned = sanitizeAttributionValue(url.searchParams.get(key) || "");
    if (cleaned && !isPlaceholderValue(cleaned)) params[key] = cleaned;
  }
  if (Object.keys(params).length) {
    const touch: AttributionTouch = {
      params,
      landing_path: url.pathname,
      referrer: sanitizeAttributionValue(document.referrer, 300),
      ts: new Date().toISOString(),
    };
    state = { first: state?.first ?? touch, last: touch };
    persist();
  } else if (!state) restore();

  if (!listening) {
    listening = true;
    window.addEventListener(CONSENT_EVENT, () => {
      if (effectiveConsent().analytics) persist();
      else removeStored();
    });
  }
  return getAttribution();
}

export function getAttribution() {
  if (!state) return null;
  const optedOut = effectiveConsent().ccpa_opt_out;
  const filterTouch = (touch: AttributionTouch): AttributionTouch => {
    const params = { ...touch.params };
    if (optedOut) {
      for (const key of CLICK_IDS)
        delete params[key as keyof AttributionParams];
    }
    return { ...touch, params };
  };
  const first = filterTouch(state.first);
  const last = filterTouch(state.last);
  return {
    ...last.params,
    landing_path: last.landing_path,
    referrer: last.referrer,
    first,
    last,
  };
}

export function resetAttributionForTests() {
  state = null;
  listening = false;
  removeStored();
}
